<template>
  <v-app id="app_overdue_collections_approved">
    <v-container fluid>

      <!-- Loading Overlay -->
      <v-overlay v-model="loading" class="align-center justify-center">
        <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
      </v-overlay>

      <!-- Header -->
      <v-row class="pl-2 pr-6">
        <v-col class="mt-3" cols="7">
          <p class="custom_page_main_title">OVERDUE COLLECTIONS — APPROVED LIST</p>
          <p class="custom_page_main_sub_title" style="opacity: 0.6">
            Review overdue collection approved data — all stages with comments and approvers.
          </p>
        </v-col>
        <v-col class="px-4 mt-5" cols="5">
          <v-row class="custom_tool_bar_style">
            <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
            <v-btn class="gray-text" size="x-large" variant="text" @click="backPageRoute()">
              <v-icon size="x-large">mdi-menu-left</v-icon>&nbsp;BACK
            </v-btn>
          </v-row>
        </v-col>
      </v-row>

      <!-- Main Card -->
      <v-card class="custom_card_box mt-4" elevation="1" border="sm opacity-1" rounded="lg">

        <!-- Filters (collapsed by default) -->
        <v-expansion-panels v-model="panelOpen" multiple>
          <v-expansion-panel value="filters" elevation="0">
            <v-expansion-panel-title expand-icon="mdi-menu-down">
              <v-icon class="mr-3">mdi-filter-menu-outline</v-icon>
              <span class="text-body-1 font-weight-bold">FILTERS</span>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-row class="mt-2" dense>

                <!-- Date Range / Single Date -->
                <v-col cols="12" md="4" class="py-1">
                  <v-date-input
                    v-model="dates"
                    prepend-inner-icon="$calendar"
                    prepend-icon=""
                    label="Select Date or Date Range (Created Date)"
                    multiple="range"
                    clearable
                    density="comfortable"
                    class="custom_input_field_color"
                    variant="outlined"
                    @update:model-value="onDateUpdate"
                    @click:clear="clearDateRange"
                  ></v-date-input>

                  <!-- Single Date Indicator -->
                  <div
                    v-if="isSingleDateMode && dates && dates.length === 1"
                    class="text-caption text-teal-darken-2 mt-1 ml-1"
                  >
                    <v-icon size="x-small" color="teal-darken-2">mdi-information-outline</v-icon>
                    Single day selected — filtering for {{ formatDateForAPI(dates[0]) }} only.
                  </div>
                  <div
                    v-else-if="dates && dates.length >= 2"
                    class="text-caption text-blue-darken-2 mt-1 ml-1"
                  >
                    <v-icon size="x-small" color="blue-darken-2">mdi-information-outline</v-icon>
                    Range selected — {{ formatDateForAPI(dates[0]) }} to {{ formatDateForAPI(dates[dates.length - 1]) }}.
                  </div>
                </v-col>

                <!-- Area - API-based -->
                <v-col cols="12" md="4" class="py-1">
                  <v-autocomplete
                    v-model="selectDistrict"
                    :items="areaLists"
                    item-title="text"
                    item-value="value"
                    label="Area"
                    placeholder="Select area"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    multiple
                    clearable
                    :loading="areasLoading"
                  >
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                      >
                        {{ item.title }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Sales Rep -->
                <v-col cols="12" md="4" class="py-1">
                  <v-select
                    v-model="selectedAgent"
                    :items="agentListsWithDetails"
                    density="comfortable"
                    clearable
                    item-title="text"
                    item-value="value"
                    class="custom_input_field_color"
                    label="Sales Rep"
                    multiple
                    placeholder="Select sales representative"
                    variant="outlined"
                    :loading="salesRepsLoading"
                  >
                    <template v-slot:item="{ props, item }">
                      <v-list-item v-bind="props" :title="undefined">
                        <template v-slot:prepend>
                          <v-checkbox-btn :model-value="props.selected"></v-checkbox-btn>
                        </template>
                        <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
                        <v-list-item-subtitle style="color: #01579B; font-size: 11px;">
                          REG NO: {{ item.raw.registration }}
                        </v-list-item-subtitle>
                      </v-list-item>
                    </template>
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                        close-icon="mdi-close-circle"
                      >
                        {{ item.raw.text }}
                      </v-chip>
                    </template>
                  </v-select>
                </v-col>

                <!-- Route -->
                <v-col cols="12" md="4" class="py-1">
                  <v-autocomplete
                    v-model="selectedRoute"
                    :items="routeTypeOptions"
                    item-title="text"
                    item-value="value"
                    label="Route"
                    placeholder="Select route"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    multiple
                    clearable
                  >
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                      >
                        {{ item.title || item.text }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Dealer -->
                <v-col cols="12" md="4" class="py-1">
                  <v-autocomplete
                    v-model="selectedDealer"
                    :items="dealerListsWithDetails"
                    clearable
                    density="comfortable"
                    item-title="text"
                    item-value="value"
                    label="Dealer"
                    class="custom_input_field_color"
                    multiple
                    placeholder="Select dealer"
                    variant="outlined"
                    :loading="dealersLoading"
                  >
                    <template v-slot:item="{ props, item }">
                      <v-list-item v-bind="props" :title="undefined">
                        <template v-slot:prepend>
                          <v-checkbox-btn :model-value="props.selected"></v-checkbox-btn>
                        </template>
                        <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
                        <v-list-item-subtitle style="color: #01579B; font-size: 11px;">
                          REG NO: {{ item.raw.registration }}
                        </v-list-item-subtitle>
                      </v-list-item>
                    </template>
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                      >
                        {{ item.title }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Collection Type -->
                <v-col cols="12" md="4" class="py-1">
                  <v-autocomplete
                    v-model="selectedCollectionType"
                    :items="collectionTypeOptions"
                    item-title="text"
                    item-value="value"
                    label="Collection Type"
                    placeholder="Select type"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    multiple
                    clearable
                  >
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                      >
                        {{ item.title }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Piyestra Company -->
                <v-col cols="12" md="4" class="py-1">
                  <v-autocomplete
                    v-model="selectedCompany"
                    :items="hardcodedCompanyLists"
                    item-title="text"
                    item-value="value"
                    label="Piyestra Company"
                    placeholder="Select company"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    multiple
                    clearable
                  >
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                      >
                        {{ item.title }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Search -->
                <v-col cols="12" md="4" class="py-1">
                  <v-text-field
                    v-model="search"
                    clearable
                    label="Search"
                    placeholder="Search by Receipt ID, Dealer Name, Area, etc."
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                  ></v-text-field>
                </v-col>

              </v-row>
              <v-divider class="mb-3 mt-2"></v-divider>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-divider thickness="2"></v-divider>

        <!-- Table -->
        <v-card-text class="table-wrapper">
          <v-data-table-server
            :headers="headers"
            :items="approvedCollections"
            :items-length="paginationInfo ? paginationInfo.total : 0"
            v-model:page="currentPage"
            v-model:items-per-page="perPage"
            :items-per-page-options="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
            ]"
            :loading="loading"
            item-value="id"
            class="px-2 custom_table_curved compact-table"
            @update:options="onTableOptionsUpdate"
            fixed-header
            height="calc(100vh - 260px)"
          >
            <template v-slot:loading>
              <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
            </template>

            <!-- Receipt No -->
            <template v-slot:[`item.receipt_no`]="{ item }">
              <p class="font-weight-medium text-body-2 mt-0">
                {{ item.receipt?.receipt_id || '-' }}
              </p>
            </template>

            <!-- Manual Receipt Number -->
            <template v-slot:[`item.manual_receipt_number`]="{ item }">
              <div>
                <v-chip color="#01579B" variant="flat" size="small" class="font-weight-medium">
                  {{ item.receipt?.manual_receipt_number || 'N/A' }}
                </v-chip>
              </div>
            </template>

            <!-- Collection Date and Time -->
            <template v-slot:[`item.collection_datetime`]="{ item }">
              <div class="column-content">
                {{ formatDateTime(item.receipt?.created_at) }}
              </div>
            </template>

            <!-- Area -->
            <template v-slot:[`item.area`]="{ item }">
              <p class="text-body-2 mt-0">
                {{ item.receipt?.area_code || '-' }}
              </p>
            </template>

            <!-- Sales Executive -->
            <template v-slot:[`item.sales_executive`]="{ item }">
              <p class="text-body-2 mt-0">
                {{ getSalesRepName(item.receipt?.saleRep_id) || '-' }}
              </p>
            </template>

            <!-- Dealer Information -->
            <template v-slot:[`item.dealer_info`]="{ item }">
              <div v-if="item.receipt?.dealer_name">
                <p class="font-weight-bold text-body-2 mt-0">{{ item.receipt.dealer_name }}</p>
                <v-divider class="mb-0 mt-0" thickness="2" color="blue-grey-lighten-4"></v-divider>
                <p style="color: #01579B; font-size: 11px" class="mb-0 font-weight-medium">Dealer Code: {{ item.receipt.dealer_code }}</p>
              </div>
              <div v-else>
                <p class="text-body-2 mt-0 text-grey">-</p>
              </div>
            </template>

            <!-- Invoice No -->
            <template v-slot:[`item.invoice_no`]="{ item }">
              <div v-if="item.receipt?.items && item.receipt.items.length > 0">
                <div v-for="(invItem, idx) in item.receipt.items" :key="idx" class="mb-0">
                  <p class="text-body-2">{{ invItem.document_no || '-' }}</p>
                </div>
              </div>
              <p v-else class="text-body-2 text-grey">-</p>
            </template>

            <!-- Invoice Date -->
            <template v-slot:[`item.invoice_date`]="{ item }">
              <div v-if="item.receipt?.items && item.receipt.items.length > 0">
                <div v-for="(invItem, idx) in item.receipt.items" :key="idx" class="mb-0">
                  <p class="text-body-2">{{ formatDateForExcel(invItem.posting_date) }}</p>
                </div>
              </div>
              <p v-else class="text-body-2 text-grey">-</p>
            </template>

            <!-- Discount Group -->
            <template v-slot:[`item.discount_group`]="{ item }">
              <p class="text-body-2 mt-0">{{ getDiscountGroup(item.receipt) }}</p>
            </template>

            <!-- Invoice Amount -->
            <template v-slot:[`item.invoice_amount`]="{ item }">
              <div v-if="item.receipt?.items && item.receipt.items.length > 0">
                <div v-for="(invItem, idx) in item.receipt.items" :key="idx" class="mb-0">
                  <p style="color: #01579B; font-size: 13px;" class="font-weight-medium">
                    LKR {{ formatNumber(invItem.amount) }}
                  </p>
                </div>
              </div>
              <p v-else class="text-body-2 text-grey">-</p>
            </template>

            <!-- Collection Amount -->
            <template v-slot:[`item.collection_amount`]="{ item }">
              <p style="color: #01579B;" class="text-body-2 font-weight-medium mt-0">
                LKR. {{ formatNumber(item.receipt?.collect_total_amount) }}
              </p>
            </template>

            <!-- Payment Method -->
            <template v-slot:[`item.payment_method`]="{ item }">
              <v-chip 
                :color="getPaymentMethodColor(item.receipt?.payment_method)" 
                size="small"
                variant="flat"
                class="font-weight-bold"
              >
                <v-icon start size="small">{{ getPaymentMethodIcon(item.receipt?.payment_method) }}</v-icon>
                {{ item.receipt?.payment_method || '-' }}
              </v-chip>
            </template>

            <!-- Receipt Status -->
            <template v-slot:[`item.receipt_status`]="{ item }">
              <v-chip 
                :color="getReceiptStatusColor(item.receipt?.receipt_status)" 
                size="small"
                variant="flat"
                class="font-weight-bold"
              >
                <v-icon start size="small">{{ getReceiptStatusIcon(item.receipt?.receipt_status) }}</v-icon>
                {{ item.receipt?.receipt_status || '-' }}
              </v-chip>
            </template>

            <!-- Overdue Date -->
            <template v-slot:[`item.overdue_date`]="{ item }">
              <p class="text-body-2 mt-0" :class="{'overdue-date': isOverdue(item.receipt)}">
                {{ getOverdueDate(item.receipt) }}
              </p>
            </template>

            <!-- Overdue Reason -->
            <template v-slot:[`item.overdue_reason`]="{ item }">
              <p class="text-body-2 mt-0" style="max-width: 180px; white-space: normal;">
                {{ item.receipt?.overdue_reason || '-' }}
              </p>
            </template>

            <!-- Workflow Status -->
            <template v-slot:[`item.workflow_status`]="{ item }">
              <v-row dense align="center" class="mt-0" style="min-width: 120px;">
                <v-col cols="4" class="text-center pa-0">
                  <v-icon
                    :color="getStageApprovalColor(item, 'AREA_MANAGER')"
                    :icon="getStageApprovalIcon(item, 'AREA_MANAGER')"
                    size="small"
                  ></v-icon>
                  <div class="text-caption">AM</div>
                </v-col>
                <v-col cols="1" class="text-center pa-0">
                  <v-icon size="x-small" color="grey">mdi-arrow-right</v-icon>
                </v-col>
                <v-col cols="4" class="text-center pa-0">
                  <v-icon
                    :color="getStageApprovalColor(item, 'REGIONAL_MANAGER')"
                    :icon="getStageApprovalIcon(item, 'REGIONAL_MANAGER')"
                    size="small"
                  ></v-icon>
                  <div class="text-caption">RM</div>
                </v-col>
                <v-col cols="1" class="text-center pa-0">
                  <v-icon size="x-small" color="grey">mdi-arrow-right</v-icon>
                </v-col>
                <v-col cols="4" class="text-center pa-0">
                  <v-icon
                    :color="getStageApprovalColor(item, 'SALES_MANAGER')"
                    :icon="getStageApprovalIcon(item, 'SALES_MANAGER')"
                    size="small"
                  ></v-icon>
                  <div class="text-caption">SM</div>
                </v-col>
              </v-row>
            </template>

            <!-- Stage -->
            <template v-slot:[`item.stage`]="{ item }">
              <div>
                <div v-if="item.current_pending_stage && !item.all_stages_approved">
                  <v-chip
                    :color="getStageColor(item.current_pending_stage)"
                    size="small"
                    variant="flat"
                    class="font-weight-bold"
                  >
                    <v-icon start size="small">mdi-timer-sand</v-icon>
                    PENDING: {{ formatStageLabel(item.current_pending_stage) }}
                  </v-chip>
                </div>
                <div v-else-if="item.all_stages_approved">
                  <v-chip color="success" size="small" variant="flat" class="font-weight-bold">
                    <v-icon start size="small">mdi-check-circle</v-icon>
                    ALL APPROVED
                  </v-chip>
                </div>
              </div>
            </template>

            <!-- Action -->
            <template v-slot:[`item.action`]="{ item }">
              <div class="d-flex flex-column align-start gap-1 py-0">
                
                <v-btn
                  class="custom_view_btn mb-1"
                  variant="flat"
                  size="small"
                  style="min-width: 130px; justify-content: flex-start;"
                  @click="viewItem(item)"
                >
                  <v-icon size="small" class="mr-1">mdi-eye</v-icon>
                  VIEW
                </v-btn>

                <v-btn
                  class="custom_view_workflow_btn"
                  variant="outlined"
                  size="small"
                  style="min-width: 130px; justify-content: flex-start;"
                  @click="showWorkflowDetail(item)"
                >
                  <v-icon size="small" class="mr-1">mdi-information-outline</v-icon>
                  WORKFLOW
                </v-btn>

              </div>
            </template>

            <!-- Empty state -->
            <template v-slot:no-data>
              <div class="text-center pa-6">
                <v-icon size="48" color="grey-lighten-1">mdi-inbox-outline</v-icon>
                <p class="mt-2 text-grey">No approved overdue collections found</p>
              </div>
            </template>

          </v-data-table-server>
        </v-card-text>

        <!-- Export Buttons (Bottom) -->
        <v-divider thickness="2"></v-divider>
        <v-card-text class="custom_tool_bar_style py-2">
          <v-btn class="custom_pdf_btn" variant="outlined" @click="exportPDF" :loading="exporting">
            <v-icon size="large">mdi-file-pdf</v-icon>&nbsp;&nbsp;PDF
          </v-btn>
          <v-btn class="custom_excel_btn ml-4" variant="outlined" @click="exportExcel" :loading="exporting">
            <v-icon size="large">mdi-file-excel</v-icon>&nbsp;&nbsp;Excel
          </v-btn>
        </v-card-text>

        <!-- Pagination Info -->
        <v-card-text class="pt-0">
          <v-row class="mt-0" v-if="paginationInfo">
            <v-col cols="12">
              <div class="text-caption text-center text-grey">
                Showing {{ paginationInfo.from }} to {{ paginationInfo.to }}
                of {{ paginationInfo.total }} entries
              </div>
            </v-col>
          </v-row>
        </v-card-text>

      </v-card>

      <!-- Workflow Details Dialog -->
      <v-dialog v-model="workflowDialog" max-height="750" max-width="650" persistent>
        <v-card rounded="md">
          <div class="pa-4">

            <v-card color="#F0F0F0" flat>
              <template v-slot:prepend>
                <v-card-title class="text-subtitle-1 font-weight-bold">
                  COMPLETE APPROVAL WORKFLOW
                </v-card-title>
              </template>
              <template v-slot:append>
                <v-btn @click="workflowDialog = false" icon="mdi-close" size="x-small"></v-btn>
              </template>
            </v-card>

            <v-card color="white" flat class="mt-3">
              <v-card-item>

                <!-- Receipt Info -->
                <v-row class="mt-2" dense>
                  <v-col cols="6">
                    <div class="text-caption text-grey">Receipt ID</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ selectedItem?.receipt?.receipt_id || '-' }}
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="text-caption text-grey">Manual Receipt No</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ selectedItem?.receipt?.manual_receipt_number || '-' }}
                    </div>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Customer</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ selectedItem?.receipt?.items?.[0]?.name || '-' }}
                    </div>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Amount</div>
                    <div class="text-body-2 font-weight-medium" style="color: #01579B">
                      LKR. {{ formatNumber(selectedItem?.receipt?.collect_total_amount) }}
                    </div>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Overdue Count</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ selectedItem?.receipt?.overdue_count || 0 }}
                    </div>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text-grey">Overdue Days</div>
                    <div class="text-body-2">{{ selectedItem?.receipt?.overdue_days_label || '-' }}</div>
                  </v-col>
                </v-row>

                <v-divider class="my-4"></v-divider>

                <div class="text-subtitle-2 font-weight-bold mb-4">
                  <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
                  Approval Workflow (All Stages)
                </div>

                <!-- Stage 1: Area Manager -->
                <v-card 
                  v-if="selectedItem?.stages_info?.AREA_MANAGER"
                  :color="selectedItem?.stages_info?.AREA_MANAGER?.status === 'APPROVED' ? '#e8f5e9' : '#fff3e0'"
                  flat 
                  class="mb-3 pa-3 border-left-4"
                  :style="{ 'border-left-color': selectedItem?.stages_info?.AREA_MANAGER?.status === 'APPROVED' ? '#4caf50' : '#ff9800' }"
                >
                  <div class="text-body-2 font-weight-bold">
                    <v-icon 
                      :color="selectedItem?.stages_info?.AREA_MANAGER?.status === 'APPROVED' ? 'success' : 'warning'"
                      :icon="selectedItem?.stages_info?.AREA_MANAGER?.status === 'APPROVED' ? 'mdi-check-circle' : 'mdi-circle-outline'"
                      size="small"
                      class="mr-2"
                    ></v-icon>
                    STAGE 1: AREA MANAGER
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <v-row dense class="mt-2">
                    <v-col cols="6">
                      <div class="text-caption text-grey">Status</div>
                      <v-chip
                        :color="selectedItem?.stages_info?.AREA_MANAGER?.status === 'APPROVED' ? 'success' : 'warning'"
                        size="x-small"
                        variant="flat"
                      >
                        {{ selectedItem?.stages_info?.AREA_MANAGER?.status }}
                      </v-chip>
                    </v-col>
                    <v-col cols="6" v-if="selectedItem?.stages_info?.AREA_MANAGER?.approved_by">
                      <div class="text-caption text-grey">Approved By</div>
                      <div class="text-body-2 font-weight-medium">{{ selectedItem?.stages_info?.AREA_MANAGER?.approved_by }}</div>
                      <div class="text-caption text-grey-darken-1">{{ selectedItem?.stages_info?.AREA_MANAGER?.approved_by_code }}</div>
                    </v-col>
                  </v-row>
                  <div v-if="selectedItem?.stages_info?.AREA_MANAGER?.approved_at" class="text-caption text-grey mt-2">
                    <v-icon size="x-small" class="mr-1">mdi-calendar</v-icon>
                    {{ formatDate(selectedItem?.stages_info?.AREA_MANAGER?.approved_at) }}
                  </div>
                  <div v-if="selectedItem?.stages_info?.AREA_MANAGER?.comment" class="mt-3 pa-3 bg-grey-lighten-4 rounded text-body-2">
                    <strong>💬 Comment:</strong>
                    <div class="mt-1">{{ selectedItem?.stages_info?.AREA_MANAGER?.comment }}</div>
                  </div>
                </v-card>

                <div v-if="selectedItem?.stages_info?.AREA_MANAGER?.status === 'APPROVED'" class="text-center mb-3">
                  <v-icon color="success">mdi-arrow-down</v-icon>
                </div>

                <!-- Stage 2: Regional Manager -->
                <v-card 
                  v-if="selectedItem?.stages_info?.REGIONAL_MANAGER"
                  :color="selectedItem?.stages_info?.REGIONAL_MANAGER?.status === 'APPROVED' ? '#e8f5e9' : '#fff3e0'"
                  flat 
                  class="mb-3 pa-3 border-left-4"
                  :style="{ 'border-left-color': selectedItem?.stages_info?.REGIONAL_MANAGER?.status === 'APPROVED' ? '#4caf50' : '#ff9800' }"
                >
                  <div class="text-body-2 font-weight-bold">
                    <v-icon 
                      :color="selectedItem?.stages_info?.REGIONAL_MANAGER?.status === 'APPROVED' ? 'success' : 'warning'"
                      :icon="selectedItem?.stages_info?.REGIONAL_MANAGER?.status === 'APPROVED' ? 'mdi-check-circle' : 'mdi-circle-outline'"
                      size="small"
                      class="mr-2"
                    ></v-icon>
                    STAGE 2: REGIONAL MANAGER
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <v-row dense class="mt-2">
                    <v-col cols="6">
                      <div class="text-caption text-grey">Status</div>
                      <v-chip
                        :color="selectedItem?.stages_info?.REGIONAL_MANAGER?.status === 'APPROVED' ? 'success' : 'warning'"
                        size="x-small"
                        variant="flat"
                      >
                        {{ selectedItem?.stages_info?.REGIONAL_MANAGER?.status }}
                      </v-chip>
                    </v-col>
                    <v-col cols="6" v-if="selectedItem?.stages_info?.REGIONAL_MANAGER?.approved_by">
                      <div class="text-caption text-grey">Approved By</div>
                      <div class="text-body-2 font-weight-medium">{{ selectedItem?.stages_info?.REGIONAL_MANAGER?.approved_by }}</div>
                      <div class="text-caption text-grey-darken-1">{{ selectedItem?.stages_info?.REGIONAL_MANAGER?.approved_by_code }}</div>
                    </v-col>
                  </v-row>
                  <div v-if="selectedItem?.stages_info?.REGIONAL_MANAGER?.approved_at" class="text-caption text-grey mt-2">
                    <v-icon size="x-small" class="mr-1">mdi-calendar</v-icon>
                    {{ formatDate(selectedItem?.stages_info?.REGIONAL_MANAGER?.approved_at) }}
                  </div>
                  <div v-if="selectedItem?.stages_info?.REGIONAL_MANAGER?.comment" class="mt-3 pa-3 bg-grey-lighten-4 rounded text-body-2">
                    <strong>💬 Comment:</strong>
                    <div class="mt-1">{{ selectedItem?.stages_info?.REGIONAL_MANAGER?.comment }}</div>
                  </div>
                </v-card>

                <div v-if="selectedItem?.stages_info?.REGIONAL_MANAGER?.status === 'APPROVED'" class="text-center mb-3">
                  <v-icon color="success">mdi-arrow-down</v-icon>
                </div>

                <!-- Stage 3: Sales Manager -->
                <v-card 
                  v-if="selectedItem?.stages_info?.SALES_MANAGER"
                  :color="selectedItem?.stages_info?.SALES_MANAGER?.status === 'APPROVED' ? '#e8f5e9' : '#fff3e0'"
                  flat 
                  class="pa-3 border-left-4"
                  :style="{ 'border-left-color': selectedItem?.stages_info?.SALES_MANAGER?.status === 'APPROVED' ? '#4caf50' : '#ff9800' }"
                >
                  <div class="text-body-2 font-weight-bold">
                    <v-icon 
                      :color="selectedItem?.stages_info?.SALES_MANAGER?.status === 'APPROVED' ? 'success' : 'warning'"
                      :icon="selectedItem?.stages_info?.SALES_MANAGER?.status === 'APPROVED' ? 'mdi-check-circle' : 'mdi-circle-outline'"
                      size="small"
                      class="mr-2"
                    ></v-icon>
                    STAGE 3: SALES MANAGER
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <v-row dense class="mt-2">
                    <v-col cols="6">
                      <div class="text-caption text-grey">Status</div>
                      <v-chip
                        :color="selectedItem?.stages_info?.SALES_MANAGER?.status === 'APPROVED' ? 'success' : 'warning'"
                        size="x-small"
                        variant="flat"
                      >
                        {{ selectedItem?.stages_info?.SALES_MANAGER?.status }}
                      </v-chip>
                    </v-col>
                    <v-col cols="6" v-if="selectedItem?.stages_info?.SALES_MANAGER?.approved_by">
                      <div class="text-caption text-grey">Approved By</div>
                      <div class="text-body-2 font-weight-medium">{{ selectedItem?.stages_info?.SALES_MANAGER?.approved_by }}</div>
                      <div class="text-caption text-grey-darken-1">{{ selectedItem?.stages_info?.SALES_MANAGER?.approved_by_code }}</div>
                    </v-col>
                  </v-row>
                  <div v-if="selectedItem?.stages_info?.SALES_MANAGER?.approved_at" class="text-caption text-grey mt-2">
                    <v-icon size="x-small" class="mr-1">mdi-calendar</v-icon>
                    {{ formatDate(selectedItem?.stages_info?.SALES_MANAGER?.approved_at) }}
                  </div>
                  <div v-if="selectedItem?.stages_info?.SALES_MANAGER?.comment" class="mt-3 pa-3 bg-grey-lighten-4 rounded text-body-2">
                    <strong>💬 Comment:</strong>
                    <div class="mt-1">{{ selectedItem?.stages_info?.SALES_MANAGER?.comment }}</div>
                  </div>
                </v-card>

                <div v-if="selectedItem?.all_stages_approved" class="mt-4 pa-3 bg-success-lighten-5 rounded">
                  <div class="text-body-2 font-weight-bold text-success">
                    <v-icon color="success" size="small" class="mr-2">mdi-check-circle</v-icon>
                    ✓ ALL STAGES APPROVED — COLLECTION COMPLETE
                  </div>
                </div>

                <div class="mt-4">
                  <v-btn class="custom_btn_secondary" @click="workflowDialog = false" block>
                    CLOSE                  </v-btn>
                </div>

              </v-card-item>
            </v-card>

          </div>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar v-model="snackbar" :timeout="5000" :color="snackbarColor">
        {{ snackbarText }}
        <template v-slot:actions>
          <v-btn color="white" variant="text" @click="snackbar = false">Close</v-btn>
        </template>
      </v-snackbar>

    </v-container>
  </v-app>
</template>

<script>
import axios from 'axios';
import { EventBus } from '@/plugins/event-bus';
import { mapActions } from 'vuex';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import _ from 'lodash';

const BASE_URL = process.env.VUE_APP_API_BASE_URL;

const ROLE_STAGE_MAP = {
  'AREA MANAGER':     'AREA_MANAGER',
  'REGIONAL MANAGER': 'REGIONAL_MANAGER',
  'SALES MANAGER':    'SALES_MANAGER',
};

const VIEW_ONLY_ROLES = [
  'ADMINISTRATOR',
  'AREA ACCOUNTANT',
];

export default {
  name: 'OverdueCollectionsApproved',

  data() {
    return {
      panelOpen: [], // Collapsed by default
      dates: [],
      search: '',
      
      selectedAgent: [],
      selectedRoute: [],
      selectedDealer: [],
      selectDistrict: [],
      selectedCollectionType: [],
      selectedCompany: [],

      workflowDialog: false,
      selectedItem: null,

      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',

      loading: false,
      exporting: false,
      salesRepsLoading: false,
      dealersLoading: false,
      areasLoading: false,

      approvedCollections: [],
      paginationInfo: null,
      currentPage: 1,
      perPage: 10,

      isInternalPageChange: true,

      userId: null,
      userType: null,
      userStage: null,
      canViewData: false,

      agentListsWithDetails: [],
      dealerListsWithDetails: [],
      areaLists: [],
      salesRepMap: {},

      // NEW: Flag to indicate single-date mode
      isSingleDateMode: false,

      routeTypeOptions: [
        { text: 'Assign Route Plan', value: 'Assign Route Plan' },
        { text: 'Direct', value: 'Direct' },
        { text: 'Own Route Plan', value: 'Own Route Plan' },
      ],

      hardcodedCompanyLists: [
        { text: 'Piyestra Furniture (Pvt) Ltd', value: 'Piyestra Furniture (Pvt) Ltd', title: 'Piyestra Furniture (Pvt) Ltd' },
        { text: 'D.R.Industries (Pvt) Ltd', value: 'D.R.Industries (Pvt) Ltd', title: 'D.R.Industries (Pvt) Ltd' },
        { text: 'D.R.Home Appliances (Pvt) Ltd', value: 'D.R.Home Appliances (Pvt) Ltd', title: 'D.R.Home Appliances (Pvt) Ltd' }
      ],

      collectionTypeOptions: [
        { text: 'CASH', value: 'CASH', title: 'CASH' },
        { text: 'BANK', value: 'BANK', title: 'BANK' },
        { text: 'CHEQUE', value: 'CHEQUE', title: 'CHEQUE' },
      ],

      headers: [
        { title: 'RECEIPT NO', value: 'receipt_no', sortable: true },
        { title: 'MANUAL RECEIPT NO', value: 'manual_receipt_number', sortable: false },
        { title: 'COLLECTION DATE & TIME', value: 'collection_datetime', sortable: true },
        { title: 'AREA', value: 'area', sortable: true },
        { title: 'SALES EXECUTIVE', value: 'sales_executive', sortable: true },
        { title: 'DEALER INFORMATION', value: 'dealer_info', sortable: false },
        { title: 'INVOICE NO', value: 'invoice_no', sortable: false },
        { title: 'INVOICE DATE', value: 'invoice_date', sortable: false },
        { title: 'DISCOUNT GROUP', value: 'discount_group', sortable: false },
        { title: 'INVOICE AMOUNT', value: 'invoice_amount', sortable: true },
        { title: 'COLLECTION AMOUNT', value: 'collection_amount', sortable: true },
        { title: 'PAYMENT METHOD', value: 'payment_method', sortable: true },
        { title: 'RECEIPT STATUS', value: 'receipt_status', sortable: true },
        { title: 'OVERDUE DATE', value: 'overdue_date', sortable: true },
        { title: 'OVERDUE REASON', value: 'overdue_reason', sortable: false },
        { title: 'WORKFLOW', value: 'workflow_status', sortable: false },
        { title: 'STAGE', value: 'stage', sortable: false },
        { title: 'ACTION', value: 'action', sortable: false },
      ],
    };
  },

  computed: {
    userRoleLabel() {
      return this.userType || 'Unknown Role';
    },

    roleChipColor() {
      const colors = {
        'AREA MANAGER':     'green-darken-1',
        'REGIONAL MANAGER': 'blue-darken-1',
        'SALES MANAGER':    'purple-darken-1',
        'ADMINISTRATOR':    'orange-darken-1',
        'AREA ACCOUNTANT':  'teal-darken-1',
      };
      return colors[this.userType] || 'grey';
    },
  },

  methods: {
    ...mapActions(['updateSharedDataApprovedCollection']),

    /* ------------------------------------------------------------------ */
    /*  PERSISTENCE HELPERS (sessionStorage)                              */
    /* ------------------------------------------------------------------ */
    _saveFiltersToSession() {
      const filters = {
        dates: this.dates,
        search: this.search,
        selectedAgent: this.selectedAgent,
        selectedRoute: this.selectedRoute,
        selectedDealer: this.selectedDealer,
        selectDistrict: this.selectDistrict,
        selectedCollectionType: this.selectedCollectionType,
        selectedCompany: this.selectedCompany,
        currentPage: this.currentPage,
        perPage: this.perPage,
        panelOpen: this.panelOpen,
        isSingleDateMode: this.isSingleDateMode,
      };
      sessionStorage.setItem('overdueApprovedFilters', JSON.stringify(filters));
    },

    _restoreFiltersFromSession() {
      const saved = sessionStorage.getItem('overdueApprovedFilters');
      if (!saved) return false;
      try {
        const filters = JSON.parse(saved);
        if (filters.dates) this.dates = filters.dates;
        if (filters.search !== undefined) this.search = filters.search;
        if (filters.selectedAgent) this.selectedAgent = filters.selectedAgent;
        if (filters.selectedRoute) this.selectedRoute = filters.selectedRoute;
        if (filters.selectedDealer) this.selectedDealer = filters.selectedDealer;
        if (filters.selectDistrict) this.selectDistrict = filters.selectDistrict;
        if (filters.selectedCollectionType) this.selectedCollectionType = filters.selectedCollectionType;
        if (filters.selectedCompany) this.selectedCompany = filters.selectedCompany;
        if (filters.currentPage) this.currentPage = filters.currentPage;
        if (filters.perPage) this.perPage = filters.perPage;
        if (filters.panelOpen) this.panelOpen = filters.panelOpen;
        if (filters.isSingleDateMode !== undefined) this.isSingleDateMode = filters.isSingleDateMode;
        return true;
      } catch (e) {
        console.warn('Failed to restore filters', e);
        return false;
      }
    },

    onDateUpdate(newValue) {
      if (!newValue || newValue.length === 0) {
        this.isSingleDateMode = false;
        this.applyFilters();
        return;
      }

      if (newValue.length === 1) {
        this.isSingleDateMode = true;
        this.applyFilters();
        return;
      }

      this.isSingleDateMode = false;
      this.applyFilters();
    },

    safeValue(value, defaultValue = '-') {
      if (value === null || value === undefined || value === '' || value === 'NA' || value === 'N/A') {
        return defaultValue;
      }
      return value;
    },

    safeString(value) {
      return this.safeValue(value, '-');
    },

    parseJsonArrayField(value) {
      if (!value) return null;
      try {
        if (typeof value === 'string' && value.trim().startsWith('[')) {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return parsed;
          }
          return [value];
        }
        if (Array.isArray(value)) {
          return value;
        }
        return [value];
      } catch (e) {
        return [value];
      }
    },

    getChequeDateForInvoice(invoice) {
      if (!invoice) return '-';
      
      const chequeDate = invoice.cheque_date;
      if (!chequeDate) return '-';
      
      try {
        if (typeof chequeDate === 'string' && chequeDate.trim().startsWith('[')) {
          const parsed = JSON.parse(chequeDate);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return this.safeString(parsed[0]);
          }
        }
        if (Array.isArray(chequeDate) && chequeDate.length > 0) {
          return this.safeString(chequeDate[0]);
        }
        return this.safeString(chequeDate);
      } catch (e) {
        return this.safeString(chequeDate);
      }
    },

    getChequeNumberForInvoice(invoice) {
      if (!invoice) return '-';
      
      const chequeNumber = invoice.cheque_number;
      if (!chequeNumber) return '-';
      
      try {
        if (typeof chequeNumber === 'string' && chequeNumber.trim().startsWith('[')) {
          const parsed = JSON.parse(chequeNumber);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return this.safeString(parsed[0]);
          }
        }
        if (Array.isArray(chequeNumber) && chequeNumber.length > 0) {
          return this.safeString(chequeNumber[0]);
        }
        return this.safeString(chequeNumber);
      } catch (e) {
        return this.safeString(chequeNumber);
      }
    },

    getBankNameForInvoice(invoice) {
      if (!invoice) return '-';
      
      const bankName = invoice.bank_name;
      if (!bankName) return '-';
      
      try {
        if (typeof bankName === 'string' && bankName.trim().startsWith('[')) {
          const parsed = JSON.parse(bankName);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return this.safeString(parsed[0]);
          }
        }
        if (Array.isArray(bankName) && bankName.length > 0) {
          return this.safeString(bankName[0]);
        }
        return this.safeString(bankName);
      } catch (e) {
        return this.safeString(bankName);
      }
    },

    formatNumber(value) {
      if (!value) return '0.00';
      const num = parseFloat(value);
      if (isNaN(num)) return '0.00';
      return num.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US', {
        year: 'numeric', month: '2-digit', day: '2-digit',
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateForAPI(dateString) {
      if (!dateString) return null;
      
      if (dateString instanceof Date) {
        const year = dateString.getFullYear();
        const month = String(dateString.getMonth() + 1).padStart(2, '0');
        const day = String(dateString.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      
      if (typeof dateString === 'string') {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      }
      
      return null;
    },

    formatDateForExcel(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    },

    clearDateRange() {
      this.dates = [];
      this.isSingleDateMode = false;
      this.applyFilters();
    },

    backPageRoute() {
      this._saveFiltersToSession();
      this.$router.push('/dashboard');
    },

    getSalesRepName(saleRepId) {
      if (!saleRepId) return '-';
      return this.salesRepMap[saleRepId] || `ID: ${saleRepId}`;
    },

    getSalespersonCode(receipt) {
      if (!receipt?.items || receipt.items.length === 0) {
        return '-';
      }
      const salespersonCode = receipt.items[0]?.salesperson_code;
      return this.safeString(salespersonCode);
    },

    getDiscountGroup(receipt) {
      if (!receipt?.items || receipt.items.length === 0) {
        return '-';
      }
      const discountGroup = receipt.items[0].discount_posting_group;
      if (discountGroup === null || discountGroup === undefined || discountGroup === '' || discountGroup === 'NA') {
        return '-';
      }
      return discountGroup;
    },

    getOverdueDate(receipt) {
      if (receipt?.items && receipt.items.length > 0 && receipt.items[0].due_date) {
        return this.formatDate(receipt.items[0].due_date);
      }
      return '-';
    },

    isOverdue(receipt) {
      return receipt?.payment_status === 'OVERDUE';
    },

    getPaymentMethodColor(method) {
      const colors = {
        'CASH': 'green-darken-2',
        'CHEQUE': 'blue-darken-2',
        'BANK': 'orange-darken-2'
      };
      return colors[method] || 'grey-darken-2';
    },

    getPaymentMethodIcon(method) {
      const icons = {
        'CASH': 'mdi-cash',
        'CHEQUE': 'mdi-bank-outline',
        'BANK': 'mdi-bank-transfer'
      };
      return icons[method] || 'mdi-help-circle';
    },

    getReceiptStatusColor(status) {
      const colors = {
        'COLLECTED': 'success',
        'REQUEST REVERSAL': 'warning',
        'REVERSED': 'error',
        'PENDING': 'grey'
      };
      return colors[status] || 'grey';
    },

    getReceiptStatusIcon(status) {
      const icons = {
        'COLLECTED': 'mdi-check-circle',
        'REQUEST REVERSAL': 'mdi-clock-alert',
        'REVERSED': 'mdi-close-circle',
        'PENDING': 'mdi-circle-outline'
      };
      return icons[status] || 'mdi-help-circle';
    },

    formatStageLabel(stage) {
      return {
        AREA_MANAGER:     'AREA MANAGER',
        REGIONAL_MANAGER: 'REGIONAL MANAGER',
        SALES_MANAGER:    'SALES MANAGER',
      }[stage] || stage || '-';
    },

    getStageColor(stage) {
      return {
        AREA_MANAGER:     'green-darken-1',
        REGIONAL_MANAGER: 'blue-darken-1',
        SALES_MANAGER:    'purple-darken-1',
      }[stage] || 'grey';
    },

    getStageApprovalColor(item, stage) {
      if (item.stages_info?.[stage]?.status === 'APPROVED') return 'success';
      if (item.current_pending_stage === stage) return 'warning';
      return 'grey-lighten-1';
    },

    getStageApprovalIcon(item, stage) {
      if (item.stages_info?.[stage]?.status === 'APPROVED') return 'mdi-check-circle';
      if (item.current_pending_stage === stage) return 'mdi-clock-alert';
      return 'mdi-circle-outline';
    },

    viewItem(item) {
      const receiptId = item.receipt?.receipt_id;
      
      if (receiptId) {
        // Save filters BEFORE navigating to view page
        this._saveFiltersToSession();
        sessionStorage.setItem('return_from_overdue_approved', 'true');
        this.$router.push(`/total-collections-view/${receiptId}`);
      } else {
        this.showSnackbar('Unable to view: Receipt ID not found', 'error');
      }
    },

    async fetchSalesRepsFromAPI() {
      try {
        this.salesRepsLoading = true;
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId') || 1;
        
        const url = `${BASE_URL}/api/getSalesRepByUserAreaCodes?userId=${userId}`;

        const response = await axios.get(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
        });

        if (response.data.success && response.data.data) {
          this.agentListsWithDetails = response.data.data.map(rep => ({
            text: this.safeString(rep.salesRep_name),
            value: rep.id,
            registration: this.safeString(rep.userCode),
            id: rep.id,
            userCode: this.safeString(rep.userCode),
            email: this.safeString(rep.email),
            areaCodes: rep.area_codes || [],
          })).sort((a, b) => a.text.localeCompare(b.text));
          
          this.salesRepMap = {};
          response.data.data.forEach(rep => {
            this.salesRepMap[rep.id] = this.safeString(rep.salesRep_name);
          });
        } else {
          this.agentListsWithDetails = [];
        }
      } catch (error) {
        console.error('Error fetching sales reps:', error);
        this.agentListsWithDetails = [];
      } finally {
        this.salesRepsLoading = false;
      }
    },

    async fetchDealersFromAPI() {
      try {
        this.dealersLoading = true;
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId') || 1;
        
        const url = `${BASE_URL}/api/getDealersByUserAreaCodes?userId=${userId}`;

        const response = await axios.get(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
        });

        if (response.data.success && response.data.data) {
          this.dealerListsWithDetails = response.data.data.map(dealer => ({
            text: this.safeString(dealer.dealer_name),
            value: this.safeString(dealer.dealer_code),
            registration: this.safeString(dealer.dealer_code),
          })).sort((a, b) => a.text.localeCompare(b.text));
        } else {
          this.dealerListsWithDetails = [];
        }
      } catch (error) {
        console.error('Error fetching dealers:', error);
        this.dealerListsWithDetails = [];
      } finally {
        this.dealersLoading = false;
      }
    },

    async fetchAreasFromAPI() {
      try {
        this.areasLoading = true;
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId') || 1;
        
        const url = `${BASE_URL}/api/area/user/${userId}`;

        const response = await axios.get(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
        });

        if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
          this.areaLists = response.data.data.map(areaCode => ({
            text: this.safeString(areaCode),
            value: this.safeString(areaCode),
            title: this.safeString(areaCode),
          })).sort((a, b) => a.text.localeCompare(b.text));
        } else {
          this.areaLists = [];
        }
      } catch (error) {
        console.error('Error fetching areas:', error);
        this.areaLists = [];
      } finally {
        this.areasLoading = false;
      }
    },

    loadUserInfo() {
      this.userId = localStorage.getItem('userId');

      const rawType    = localStorage.getItem('userType') || '';
      this.userType    = rawType.toUpperCase().trim();
      this.userStage   = ROLE_STAGE_MAP[this.userType] || null;

      if (this.userStage) {
        this.userStage = this.userStage.toUpperCase().trim();
      }

      this.canViewData =
        !!this.userStage ||
        VIEW_ONLY_ROLES.includes(this.userType);

      console.log('=== USER INFO (APPROVED) ===');
      console.log('userType:', this.userType);
      console.log('userStage:', this.userStage);
      console.log('canViewData:', this.canViewData);
      console.log('===========================');
    },

    buildFilterParams() {
      const params = {};
      const userId = this.userId || localStorage.getItem('userId') || 1;
      
      params.userId = parseInt(userId);

      if (this.userStage) {
        params.stage = this.userStage;
      }

      if (this.selectedAgent && this.selectedAgent.length > 0) {
        params.saleRep_id = this.selectedAgent.join(',');
      }

      if (this.selectedRoute && this.selectedRoute.length > 0) {
        params.route_type = this.selectedRoute.join(',');
      }

      if (this.selectedDealer && this.selectedDealer.length > 0) {
        params.dealer_code = this.selectedDealer.join(',');
      }

      if (this.selectDistrict && this.selectDistrict.length > 0) {
        params.area_code = this.selectDistrict.join(',');
      }

      if (this.selectedCollectionType && this.selectedCollectionType.length > 0) {
        params.payment_method = this.selectedCollectionType.join(',');
      }

      if (this.selectedCompany && this.selectedCompany.length > 0) {
        params.ar_company = this.selectedCompany.join(',');
      }

      if (this.dates && this.dates.length > 0) {
        if (this.dates.length === 1 || this.isSingleDateMode) {
          const singleDate = this.formatDateForAPI(this.dates[0]);
          if (singleDate) {
            params.date_from = singleDate;
            params.date_to = singleDate;
          }
        } else if (this.dates.length >= 2) {
          const fromDate = this.formatDateForAPI(this.dates[0]);
          const toDate = this.formatDateForAPI(this.dates[this.dates.length - 1]);
          if (fromDate) params.date_from = fromDate;
          if (toDate) params.date_to = toDate;
        }
      }

      if (this.search && this.search.trim().length > 0) {
        params.search = this.search.trim();
      }

      return params;
    },

    async fetchAllFilteredData() {
      const token = localStorage.getItem('token');
      const url = `${BASE_URL}/api/receipt-approvals/approved`;
      const baseParams = this.buildFilterParams();
      
      const maxPerPage = 500;
      
      console.log('📤 Fetching all filtered data for export with params:', baseParams);

      try {
        let allData = [];
        let currentPage = 1;
        let lastPage = 1;

        const firstParams = {
          ...baseParams,
          perPage: maxPerPage,
          page: currentPage,
        };

        console.log(`📤 Fetching page ${currentPage}...`);
        
        const firstResponse = await axios.get(url, {
          params: firstParams,
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
        });

        if (firstResponse.data.success && firstResponse.data.data) {
          allData = [...firstResponse.data.data];
          lastPage = firstResponse.data.pagination.last_page;
          console.log(`📊 Total pages: ${lastPage}, Total records: ${firstResponse.data.pagination.total}`);
          
          if (lastPage > 1) {
            const remainingPages = [];
            for (let page = 2; page <= lastPage; page++) {
              remainingPages.push(page);
            }
            
            const pagePromises = remainingPages.map(async (page) => {
              const params = {
                ...baseParams,
                perPage: maxPerPage,
                page: page,
              };
              
              console.log(`📤 Fetching page ${page} of ${lastPage}...`);
              
              const response = await axios.get(url, {
                params,
                headers: {
                  Authorization: token ? `Bearer ${token}` : '',
                  'Content-Type': 'application/json',
                },
              });
              
              if (response.data.success && response.data.data) {
                return response.data.data;
              }
              return [];
            });
            
            const allPageResults = await Promise.all(pagePromises);
            
            allPageResults.forEach(pageData => {
              allData = [...allData, ...pageData];
            });
          }
          
          console.log(`✅ Fetched total ${allData.length} records for export`);
          return allData;
        } else {
          console.warn('⚠️ No data found for export');
          return [];
        }
      } catch (error) {
        console.error('❌ Error fetching all filtered data:', error);
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
        }
        throw error;
      }
    },

    getFilterSummary() {
      const filters = [];
      
      if (this.userRoleLabel && this.userRoleLabel !== 'Unknown Role') {
        filters.push(`Role: ${this.userRoleLabel}`);
      }
      if (this.userStage) {
        filters.push(`Approval Stage: ${this.formatStageLabel(this.userStage)}`);
      }
      if (this.selectedAgent && this.selectedAgent.length > 0) {
        const agentNames = this.selectedAgent.map(id => {
          const agent = this.agentListsWithDetails.find(a => a.value === id);
          return agent ? agent.text : id;
        });
        filters.push(`Sales Rep: ${agentNames.join(', ')}`);
      }
      if (this.selectedRoute && this.selectedRoute.length > 0) {
        filters.push(`Route: ${this.selectedRoute.join(', ')}`);
      }
      if (this.selectedDealer && this.selectedDealer.length > 0) {
        const dealerNames = this.selectedDealer.map(code => {
          const dealer = this.dealerListsWithDetails.find(d => d.value === code);
          return dealer ? dealer.text : code;
        });
        filters.push(`Dealer: ${dealerNames.join(', ')}`);
      }
      if (this.selectDistrict && this.selectDistrict.length > 0) {
        filters.push(`Area: ${this.selectDistrict.join(', ')}`);
      }
      if (this.selectedCollectionType && this.selectedCollectionType.length > 0) {
        filters.push(`Collection Type: ${this.selectedCollectionType.join(', ')}`);
      }
      if (this.selectedCompany && this.selectedCompany.length > 0) {
        const companyNames = this.selectedCompany.map(code => {
          const company = this.hardcodedCompanyLists.find(c => c.value === code);
          return company ? company.text : code;
        });
        filters.push(`Company: ${companyNames.join(', ')}`);
      }
      if (this.dates && this.dates.length > 0) {
        if (this.dates.length === 1 || this.isSingleDateMode) {
          filters.push(`Date: ${this.formatDateForAPI(this.dates[0])}`);
        } else if (this.dates.length >= 2) {
          const fromDate = this.formatDateForAPI(this.dates[0]);
          const toDate = this.formatDateForAPI(this.dates[this.dates.length - 1]);
          filters.push(`Date Range: ${fromDate} to ${toDate}`);
        }
      }
      if (this.search && this.search.trim()) {
        filters.push(`Search: "${this.search}"`);
      }
      
      return filters.length > 0 ? filters.join(' | ') : 'No filters applied';
    },

    getFinalApprover(item) {
      if (item.stages_info?.SALES_MANAGER?.status === 'APPROVED' && item.stages_info?.SALES_MANAGER?.approved_by) {
        return this.safeString(item.stages_info.SALES_MANAGER.approved_by);
      }
      if (item.stages_info?.REGIONAL_MANAGER?.status === 'APPROVED' && item.stages_info?.REGIONAL_MANAGER?.approved_by) {
        return this.safeString(item.stages_info.REGIONAL_MANAGER.approved_by);
      }
      if (item.stages_info?.AREA_MANAGER?.status === 'APPROVED' && item.stages_info?.AREA_MANAGER?.approved_by) {
        return this.safeString(item.stages_info.AREA_MANAGER.approved_by);
      }
      if (item.final_approver) {
        return this.safeString(item.final_approver);
      }
      if (item.receipt?.approved_by) {
        return this.safeString(item.receipt.approved_by);
      }
      return '-';
    },

    /* ------------------------------------------------------------------ */
    /*  LOGO LOADING HELPER                                                */
    /* ------------------------------------------------------------------ */
    loadImageAsBase64(imagePath) {
      return new Promise((resolve) => {
        try {
          const img = new Image();
          img.crossOrigin = 'Anonymous';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            try {
              resolve(canvas.toDataURL('image/png'));
            } catch (e) {
              console.warn('Canvas toDataURL failed:', e);
              resolve(null);
            }
          };
          img.onerror = () => {
            console.warn('Failed to load image:', imagePath);
            resolve(null);
          };
          img.src = imagePath;
        } catch (e) {
          console.warn('Error loading image:', e);
          resolve(null);
        }
      });
    },

    async exportPDF() {
      console.log('📄 PDF Export button clicked');
      
      if (this.$toast) {
        this.$toast.info('Preparing PDF export...');
      }
      
      this.exporting = true;
      
      try {
        const allData = await this.fetchAllFilteredData();
        
        console.log('📊 Data fetched for PDF:', allData ? allData.length : 0, 'records');
        
        if (!allData || allData.length === 0) {
          if (this.$toast) {
            this.$toast.warning('No data available to export');
          }
          this.exporting = false;
          return;
        }
        
        const doc = new jsPDF('landscape', 'mm', 'a4');

        const pageWidth = doc.internal.pageSize.getWidth();

        // ─── Logo section (FIRST PAGE ONLY) ───────────────────────────
        let headerY = 10;

        try {
          const logo1Base64 = await this.loadImageAsBase64(
            require('@/img/company_logo/logo-dark 2.png')
          );
          const logo2Base64 = await this.loadImageAsBase64(
            require('@/img/company_logo/g-sfa.png')
          );

          if (logo1Base64) {
            doc.addImage(logo1Base64, 'PNG', 14, headerY, 30, 12);
          }
          if (logo2Base64) {
            doc.addImage(logo2Base64, 'PNG', 48, headerY, 30, 12);
          }
        } catch (logoError) {
          console.warn('⚠️ Could not load logos for PDF:', logoError);
        }
        // ──────────────────────────────────────────────────────────────

        // ===== HEADER (centered) =====
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text('Overdue Collections — Approved List', pageWidth / 2, headerY + 5, { align: 'center' });
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(
          `Generated on: ${new Date().toLocaleString()}`,
          pageWidth / 2,
          headerY + 13,
          { align: 'center' }
        );
        
        let filterY = headerY + 22;
        doc.setFontSize(8);
        const filterSummary = this.getFilterSummary();
        const splitFilterText = doc.splitTextToSize(`Filters applied: ${filterSummary}`, pageWidth - 28);
        doc.text(splitFilterText, 14, filterY);
        filterY += (splitFilterText.length * 4) + 4;
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(`Total Records: ${allData.length}`, 14, filterY);
        filterY += 8;
        
        doc.setFont(undefined, 'normal');

        // ===== SINGLE SOURCE OF TRUTH: HEADERS (18 columns) =====
        const tableColumns = [
          '#',
          'Receipt ID',
          'Manual\nReceipt No',
          'Collection\nDate & Time',
          'Area',
          'Company',
          'Sales\nEx. Code',
          'Sales Executive',
          'Dealer Code',
          'Dealer Name',
          'Dis.\nGroup',
          'Invoice No',
          'Invoice\nAmount',
          'Collection\nAmount',
          'Payment\nMethod',
          'Receipt\nStatus',
          'Overdue\nDays',
          'Status'
        ];

        // ===== BUILD ROWS — EXACTLY 18 VALUES, SAME ORDER =====
        const tableData = [];

        allData.forEach((item, mainIndex) => {
          const manualReceiptNo = this.safeString(item.receipt?.manual_receipt_number);
          const salespersonCode = this.getSalespersonCode(item.receipt);

          const invoiceNumbers = [];
          const invoiceAmounts = [];

          if (item.receipt?.items && item.receipt.items.length > 0) {
            item.receipt.items.forEach((invoice) => {
              invoiceNumbers.push(this.safeString(invoice.document_no));
              invoiceAmounts.push(`LKR ${this.formatNumber(invoice.amount)}`);
            });
          }

          const statusLabel = item.all_stages_approved
            ? 'COMPLETE'
            : `PENDING: ${this.formatStageLabel(item.current_pending_stage)}`;

          tableData.push([
            mainIndex + 1,
            this.safeString(item.receipt?.receipt_id),
            manualReceiptNo,
            this.formatDateTime(item.receipt?.created_at),
            this.safeString(item.receipt?.area_code),
            this.safeString(item.receipt?.ar_company),
            salespersonCode,
            this.safeString(item.receipt?.dealer_name),
            this.safeString(item.receipt?.dealer_code),
            this.safeString(item.receipt?.dealer_name),
            this.getDiscountGroup(item.receipt),
            invoiceNumbers.join(', ') || '-',
            invoiceAmounts.join(', ') || '-',
            `LKR ${this.formatNumber(item.receipt?.collect_total_amount)}`,
            this.safeString(item.receipt?.payment_method),
            this.safeString(item.receipt?.receipt_status),
            this.safeString(item.receipt?.overdue_days_label || '0'),
            statusLabel
          ]);
        });

        // ===== COLUMN WIDTHS — SCALED TO FIT LANDSCAPE A4 =====
        const baseColumnStyles = {
          0:  { cellWidth: 8,  halign: 'center' },
          1:  { cellWidth: 20 },
          2:  { cellWidth: 18 },
          3:  { cellWidth: 22 },
          4:  { cellWidth: 12 },
          5:  { cellWidth: 28 },
          6:  { cellWidth: 14 },
          7:  { cellWidth: 24 },
          8:  { cellWidth: 16 },
          9:  { cellWidth: 30 },
          10: { cellWidth: 12 },
          11: { cellWidth: 22 },
          12: { cellWidth: 18 },
          13: { cellWidth: 18, halign: 'right' },
          14: { cellWidth: 14 },
          15: { cellWidth: 14 },
          16: { cellWidth: 12, halign: 'center' },
          17: { cellWidth: 26 }
        };

        const totalWidth = Object.values(baseColumnStyles).reduce((s, c) => s + c.cellWidth, 0);
        const targetWidth = 281;
        const scale = targetWidth / totalWidth;

        const columnStyles = {};
        Object.keys(baseColumnStyles).forEach((k) => {
          columnStyles[k] = {
            ...baseColumnStyles[k],
            cellWidth: Math.floor(baseColumnStyles[k].cellWidth * scale)
          };
        });

        doc.autoTable({
          head: [tableColumns],
          body: tableData,
          startY: filterY,
          styles: {
            fontSize: 6.5,
            cellPadding: 1.5,
            overflow: 'linebreak',
            valign: 'middle'
          },
          headStyles: {
            fillColor: [1, 87, 155],
            textColor: [255, 255, 255],
            fontSize: 7,
            fontStyle: 'bold',
            halign: 'center',
            valign: 'middle'
          },
          alternateRowStyles: { fillColor: [245, 245, 245] },
          columnStyles: columnStyles,
          margin: { left: 8, right: 8, top: 10, bottom: 10 },
          tableWidth: 'wrap',
          showHead: 'everyPage',
          pageBreak: 'auto',
          didDrawPage: () => {
            const pageCount = doc.internal.getNumberOfPages();
            const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
            doc.setFontSize(8);
            doc.setTextColor(120);
            doc.text(
              `Page ${pageNum} of ${pageCount}`,
              doc.internal.pageSize.getWidth() - 25,
              doc.internal.pageSize.getHeight() - 5
            );
            doc.setTextColor(0);
          }
        });

        // ⚠️ NO logo loop — logos appear ONLY on the first page

        const fileName = `overdue_collections_approved_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.pdf`;
        doc.save(fileName);
        
        console.log('✅ PDF saved as:', fileName);
        
        if (this.$toast) {
          this.$toast.success(`PDF exported with ${allData.length} records`);
        }
        
        this.logDocumentDownload(fileName, 'pdf');
        
      } catch (error) {
        console.error('❌ Error exporting PDF:', error);
        if (this.$toast) {
          this.$toast.error('Failed to export PDF: ' + (error.message || 'Unknown error'));
        }
      } finally {
        this.exporting = false;
      }
    },

    async exportExcel() {
      console.log('📊 Excel Export button clicked');
      
      if (this.$toast) {
        this.$toast.info('Preparing Excel export...');
      }
      
      this.exporting = true;
      
      try {
        const allData = await this.fetchAllFilteredData();
        
        console.log('📊 Data fetched for Excel:', allData ? allData.length : 0, 'records');
        
        if (!allData || allData.length === 0) {
          if (this.$toast) {
            this.$toast.warning('No data available to export');
          }
          this.exporting = false;
          return;
        }
        
        const wsData = [];

        wsData.push([
          'No',
          'Receipt ID',
          'Manual Receipt No',
          'Approved Date',
          'Area Code',
          'Company Name',
          'Sales Ex. Code',
          'Sales Executive Name',
          'Customer No',
          'Customer',
          'Customer Dis.Group',
          'Invoice No',
          'Invoice Date',
          'Invoice Amount',
          'Amount (LKR)',
          'Payment Method',
          'Receipt Status',
          'Over Due Days',
          'Overdue Reason',
          'Cheque Date',
          'Cheque No',
          'Bank Name',
          'Status',
          'Final Approver',
          'Workflow'
        ]);

        allData.forEach((item, index) => {
          const manualReceiptNo = this.safeString(item.receipt?.manual_receipt_number);
          const salespersonCode = this.getSalespersonCode(item.receipt);
          
          let invoiceNumbers = [];
          let invoiceAmounts = [];
          let invoiceDates = [];
          let chequeDates = [];
          let chequeNumbers = [];
          let bankNames = [];
          
          if (item.receipt?.items && item.receipt.items.length > 0) {
            item.receipt.items.forEach((invoice) => {
              invoiceNumbers.push(this.safeString(invoice.document_no));
              invoiceAmounts.push(`LKR ${this.formatNumber(invoice.amount)}`);
              invoiceDates.push(this.formatDateForExcel(invoice.posting_date));
              chequeDates.push(this.getChequeDateForInvoice(invoice));
              chequeNumbers.push(this.getChequeNumberForInvoice(invoice));
              bankNames.push(this.getBankNameForInvoice(invoice));
            });
          }
          
          wsData.push([
            index + 1,
            this.safeString(item.receipt?.receipt_id),
            manualReceiptNo,
            this.formatDateForExcel(item.approved_at || item.created_at || new Date()),
            this.safeString(item.receipt?.area_code),
            this.safeString(item.receipt?.ar_company),
            salespersonCode,
            this.safeString(item.receipt?.dealer_name),
            this.safeString(item.receipt?.dealer_code),
            this.safeString(item.receipt?.dealer_name),
            this.getDiscountGroup(item.receipt),
            invoiceNumbers.join(', ') || '-',
            invoiceDates.join(', ') || '-',
            invoiceAmounts.join(', ') || '-',
            `LKR ${this.formatNumber(item.receipt?.collect_total_amount)}`,
            this.safeString(item.receipt?.payment_method),
            this.safeString(item.receipt?.receipt_status),
            this.safeString(item.receipt?.overdue_days_label || '0'),
            this.safeString(item.receipt?.overdue_reason),
            chequeDates.join(', ') || '-',
            chequeNumbers.join(', ') || '-',
            bankNames.join(', ') || '-',
            item.all_stages_approved ? 'COMPLETE' : `PENDING: ${this.formatStageLabel(item.current_pending_stage)}`,
            this.getFinalApprover(item),
            'See details for complete workflow'
          ]);
        });

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        
        ws['!cols'] = [
          { wch: 6 },
          { wch: 18 },
          { wch: 20 },
          { wch: 15 },
          { wch: 12 },
          { wch: 35 },
          { wch: 18 },
          { wch: 25 },
          { wch: 15 },
          { wch: 30 },
          { wch: 18 },
          { wch: 25 },
          { wch: 15 },
          { wch: 18 },
          { wch: 18 },
          { wch: 15 },
          { wch: 18 },
          { wch: 15 },
          { wch: 30 },
          { wch: 15 },
          { wch: 15 },
          { wch: 20 },
          { wch: 18 },
          { wch: 22 },
          { wch: 30 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Update Approved Late Collection');
        const fileName = `overdue_collections_approved_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
        XLSX.writeFile(wb, fileName);

        console.log('✅ Excel saved as:', fileName);

        if (this.$toast) {
          this.$toast.success(`Excel exported with ${allData.length} records`);
        }

        this.logDocumentDownload(fileName, 'excel');
        
      } catch (error) {
        console.error('❌ Error exporting Excel:', error);
        if (this.$toast) {
          this.$toast.error('Failed to export Excel: ' + (error.message || 'Unknown error'));
        }
      } finally {
        this.exporting = false;
      }
    },

    async fetchCollectionsData(page = 1) {
      if (!this.canViewData) {
        this.approvedCollections = [];
        return;
      }

      this.loading = true;
      try {
        const token = localStorage.getItem('token');

        const params = {
          userId:  parseInt(this.userId),
          perPage: this.perPage,
          page,
        };

        if (this.userStage) {
          params.stage = this.userStage;
        }

        if (this.selectedAgent && this.selectedAgent.length > 0) {
          params.saleRep_id = this.selectedAgent.join(',');
        }

        if (this.selectedRoute && this.selectedRoute.length > 0) {
          params.route_type = this.selectedRoute.join(',');
        }

        if (this.selectedDealer && this.selectedDealer.length > 0) {
          params.dealer_code = this.selectedDealer.join(',');
        }

        if (this.selectDistrict && this.selectDistrict.length > 0) {
          params.area_code = this.selectDistrict.join(',');
        }

        if (this.selectedCollectionType && this.selectedCollectionType.length > 0) {
          params.payment_method = this.selectedCollectionType.join(',');
        }

        if (this.selectedCompany && this.selectedCompany.length > 0) {
          params.ar_company = this.selectedCompany.join(',');
        }

        if (this.dates && this.dates.length > 0) {
          if (this.dates.length === 1 || this.isSingleDateMode) {
            const singleDate = this.formatDateForAPI(this.dates[0]);
            if (singleDate) {
              params.date_from = singleDate;
              params.date_to = singleDate;
            }
          } else if (this.dates.length >= 2) {
            const fromDate = this.formatDateForAPI(this.dates[0]);
            const toDate = this.formatDateForAPI(this.dates[this.dates.length - 1]);
            if (fromDate) params.date_from = fromDate;
            if (toDate) params.date_to = toDate;
          }
        }

        if (this.search && this.search.trim().length > 0) {
          params.search = this.search.trim();
        }

        console.log('📤 Approved API Parameters:', params);

        const response = await axios.get(`${BASE_URL}/api/receipt-approvals/approved`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log('Approved API response:', response.data);

        if (response.data.success) {
          this.approvedCollections = response.data.data;
          this.paginationInfo      = response.data.pagination;
          this.isInternalPageChange = true;
          this.currentPage         = response.data.pagination?.current_page || page;
        } else {
          throw new Error(response.data.message || 'Failed to fetch data');
        }

      } catch (error) {
        console.error('fetchCollectionsData error:', error);
        this.showSnackbar(
          error.response?.data?.message || 'Error fetching approved collections.',
          'error'
        );
        this.approvedCollections = [];
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      this.isInternalPageChange = true;
      this.currentPage = 1;
      this.fetchCollectionsData(1);
    },

    onTableOptionsUpdate({ page, itemsPerPage }) {
      if (this.isInternalPageChange) {
        this.isInternalPageChange = false;
        return;
      }
      this.perPage = itemsPerPage;
      this.currentPage = page;
      this.fetchCollectionsData(page);
      this._saveFiltersToSession();
    },

    showWorkflowDetail(item) {
      this.selectedItem = item;
      this.workflowDialog = true;
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText  = text;
      this.snackbarColor = color;
      this.snackbar      = true;
    },

    async logDocumentDownload(fileName, fileType) {
      try {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        await axios.post(
          `${BASE_URL}/api/log-document-download`,
          { user_id: userId, file_type: fileType, file_name: fileName, entity_area: 'Overdue Collections Approved' },
          { headers: { Authorization: token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' } },
        );
      } catch (error) {
        console.error('❌ Error logging download:', error);
      }
    },

    setupEventListeners() {
      EventBus.on('overdueCollectionApproved', () => {
        this.fetchCollectionsData(this.currentPage);
      });
      EventBus.on('overdueCollectionApprovedRefresh', () => {
        this.fetchCollectionsData(this.currentPage);
      });
    },

    removeEventListeners() {
      EventBus.off('overdueCollectionApproved');
      EventBus.off('overdueCollectionApprovedRefresh');
    },
  },

  watch: {
    search: {
      handler: _.debounce(function() {
        console.log('🔎 Search watcher triggered - calling API with search param');
        this.applyFilters();
        this._saveFiltersToSession();
      }, 500),
    },
    
    selectedAgent:          { handler() { this.applyFilters(); this._saveFiltersToSession(); }, deep: true },
    selectedRoute:          { handler() { this.applyFilters(); this._saveFiltersToSession(); }, deep: true },
    selectedDealer:         { handler() { this.applyFilters(); this._saveFiltersToSession(); }, deep: true },
    selectDistrict:         { handler() { this.applyFilters(); this._saveFiltersToSession(); }, deep: true },
    selectedCollectionType: { handler() { this.applyFilters(); this._saveFiltersToSession(); }, deep: true },
    selectedCompany:        { handler() { this.applyFilters(); this._saveFiltersToSession(); }, deep: true },
    
    dates: {
      handler(newVal) {
        if (!newVal || newVal.length === 0) {
          this.isSingleDateMode = false;
        } else if (newVal.length === 1) {
          this.isSingleDateMode = true;
        } else {
          this.isSingleDateMode = false;
        }
        this.applyFilters();
        this._saveFiltersToSession();
      },
      deep: true,
    },

    panelOpen: {
      handler() {
        this._saveFiltersToSession();
      },
      deep: true,
    },
  },

  mounted() {
    this.loadUserInfo();

    // Restore filters before fetching data
    const restored = this._restoreFiltersFromSession();

    this.setupEventListeners();
    
    this.fetchSalesRepsFromAPI();
    this.fetchDealersFromAPI();
    this.fetchAreasFromAPI();
    
    // Use restored page if available
    const targetPage = restored && this.currentPage ? this.currentPage : 1;
    this.fetchCollectionsData(targetPage);
  },

  beforeUnmount() {
    // Save filters one last time
    this._saveFiltersToSession();
    this.removeEventListeners();
  },
};
</script>

<style>
@import "../../styles/collection_management/collection_management.scss";

.custom_view_btn {
  background-color: rgb(128, 0, 128) !important;
  color: white !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  border-radius: 8px;
  padding: 0 16px;
  height: 30px;
  box-shadow: 0 2px 4px rgba(128, 0, 128, 0.2);
}

.custom_view_btn:hover:not(:disabled) {
  background-color: rgb(100, 0, 100) !important;
  box-shadow: 0 4px 8px rgba(128, 0, 128, 0.3);
}

.custom_view_btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.custom_view_workflow_btn {
  border-color: #1976d2 !important;
  color: #1976d2 !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  border-radius: 8px;
  padding: 0 16px;
  height: 30px;
}

.custom_view_workflow_btn:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
}

.custom_table_curved .v-data-table__wrapper table {
  border-collapse: separate;
  border-spacing: 0 4px;
}

.custom_table_curved .v-data-table__wrapper table tbody tr {
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.custom_table_curved .v-data-table__wrapper table tbody tr td:first-child {
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
}

.custom_table_curved .v-data-table__wrapper table tbody tr td:last-child {
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
}

.v-data-table tbody tr:hover {
  background-color: #f5f5f5 !important;
  transition: background-color 0.2s ease;
}

.v-chip {
  font-weight: 500;
  text-transform: uppercase;
  font-size: 11px;
}

.v-list-item-subtitle {
  font-size: 11px !important;
  line-height: 1.2 !important;
  opacity: 0.9;
  margin-top: 0 !important;
}

.custom_btn_secondary {
  background-color: #f0f0f0 !important;
  color: #333 !important;
  font-weight: 500;
  border-radius: 8px;
}

.custom_btn_secondary:hover {
  background-color: #e0e0e0 !important;
}

.border-left-4 {
  border-left: 4px solid !important;
}

.bg-grey-lighten-4 {
  background-color: #f5f5f5;
}

.bg-success-lighten-5 {
  background-color: #e8f5e9;
}

.overdue-date {
  color: #BB0000 !important;
  font-weight: 500;
}

.column-content {
  font-size: 13px;
  color: #616161;
}

/* Compact table styles */
.compact-table .v-data-table__td {
  padding: 4px 8px !important;
  height: auto !important;
}

.compact-table .v-data-table__th {
  padding: 6px 8px !important;
  height: auto !important;
}

.compact-table .v-data-table__tr {
  line-height: 1.2 !important;
}

.compact-table .v-data-table__tbody tr td {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

.compact-table p {
  margin-bottom: 0 !important;
}

/* Table wrapper styles for fixed header and scrolling */
.table-wrapper {
  position: relative;
  overflow: hidden;
}

.v-data-table .v-table__wrapper {
  overflow-y: auto !important;
  overflow-x: auto !important;
}

.v-data-table .v-table__thead {
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
}
</style>